import React from "react";
import { AiFillClockCircle } from "react-icons/ai";
import { BsFillCalendar2DateFill } from "react-icons/bs";
import { MdPhoneInTalk, MdVideoCall, MdLocationOn } from "react-icons/md";
import { FaUserMd } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

export default function AppointmentCard({ pics, name, post, time, date, mode, status }) {
  const location = useLocation();
  const phone = JSON.parse(localStorage.getItem("hospital_phone"));
  const isPastAppointment = location.pathname === "/appointment-past";
  
  // Format date for better display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time for better display
  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  // Get status color and text
  const getStatusInfo = () => {
    if (isPastAppointment) {
      return {
        color: "bg-gray-100 text-gray-700",
        text: "Completed"
      };
    }
    
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return { color: "bg-green-100 text-green-700", text: "Confirmed" };
      case 'pending':
        return { color: "bg-yellow-100 text-yellow-700", text: "Pending" };
      case 'cancelled':
        return { color: "bg-red-100 text-red-700", text: "Cancelled" };
      default:
        return { color: "bg-blue-100 text-blue-700", text: "Scheduled" };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-primary100/20 mx-4 mb-4 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
      <Link to={mode === 'online' ? '/appointment/check' : ''} className="block">
        <div className="p-4">
          {/* Header Section */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary100 to-emerald-600 p-0.5">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                    {pics ? (
                      <img 
                        src={pics} 
                        alt={name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : (
                      <FaUserMd className="text-primary100 text-2xl" />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-800 truncate mb-1" title={name}>
                  {name || "Healthcare Provider"}
                </h3>
                <div className="flex items-center justify-between">
                  <p className="text-gray-500 text-sm flex items-center truncate">
                    <MdLocationOn className="mr-1 text-xs flex-shrink-0" />
                    <span className="truncate">{post || "Clinic"}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Status Badge - moved to top right */}
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color} flex-shrink-0 ml-2`}>
              {statusInfo.text}
            </div>
          </div>

          {/* Appointment Details */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-primary100/10 rounded-xl p-3 flex items-center space-x-2 border border-primary100/20">
              <div className="p-2 bg-primary100 rounded-lg">
                <AiFillClockCircle className="text-white text-sm" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 font-medium">Time</p>
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {formatTime(time)}
                </p>
              </div>
            </div>

            <div className="bg-emerald-50 rounded-xl p-3 flex items-center space-x-2 border border-emerald-200">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <BsFillCalendar2DateFill className="text-white text-sm" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 font-medium">Date</p>
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {formatDate(date)}
                </p>
              </div>
            </div>
          </div>

          {/* Action Section */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              {mode === 'online' ? (
                <div className="flex items-center space-x-1 text-blue-600">
                  <MdVideoCall className="text-lg flex-shrink-0" />
                  <span className="text-sm font-medium truncate">Video Call</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-green-600">
                  <MdLocationOn className="text-lg flex-shrink-0" />
                  <span className="text-sm font-medium truncate">In-Person</span>
                </div>
              )}
            </div>

            {!isPastAppointment && phone && (
              <a 
                href={`tel:${phone}`}
                className="flex items-center space-x-1 bg-primary100 text-white px-3 py-2 rounded-lg hover:bg-emerald-600 transition-colors flex-shrink-0 ml-2"
                onClick={(e) => e.stopPropagation()}
              >
                <MdPhoneInTalk className="text-sm" />
                <span className="text-xs font-medium">Call</span>
              </a>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
